<template>
  <v-card>
    <v-toolbar color="secondary" class="elevation-0">
      <v-toolbar-title> Ledger Chart </v-toolbar-title>
    </v-toolbar>
    <v-card-actions class="mt-5">
      <v-row>
        <v-col>
          <div>
            <v-menu :close-on-content-click="false" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-on="on"
                  dense
                  outlined
                  :value="startDate"
                  prepend-inner-icon="mdi-calendar-clock"
                  append-inner-icon="mdi-menu-down"
                  class="mx-5"
                  style="height: 40px; min-width: 180px"
                  label="From"
                  hide-details
                />
              </template>
              <v-list class="pa-0" style="max-width: fit-content">
                <v-list-item class="pa-0">
                  <v-list-item-title>
                    <v-date-picker
                      v-model="startDate"
                      show-adjacent-months
                      :max="endDate"
                    />
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-col>

        <v-col>
          <div>
            <v-menu :close-on-content-click="false" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-on="on"
                  dense
                  outlined
                  :value="endDate"
                  prepend-inner-icon="mdi-calendar-clock"
                  append-inner-icon="mdi-menu-down"
                  class="mx-5"
                  style="height: 40px; min-width: 180px"
                  label="To"
                  hide-details
                />
              </template>
              <v-list class="pa-0" style="max-width: fit-content">
                <v-list-item class="pa-0">
                  <v-list-item-title>
                    <v-date-picker
                      v-model="endDate"
                      show-adjacent-months
                      :min="startDate"
                    />
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-col>

        <v-col>
          <v-autocomplete
            v-model="frequency"
            :items="[
              { text: 'Monthly', value: 'ME' },
              { text: 'Daily', value: 'D' },
            ]"
            dense
            outlined
            label="Frequency"
            hide-details
          />
        </v-col>
        <v-col>
          <v-btn
            class="grey darken-1"
            :disabled="
              !eventType || selectedData.keys.includes(determinedLabel)
            "
            @click="addToChart"
          >
            <v-icon class="mr-3">mdi-plus</v-icon>
            Add to Chart
          </v-btn>
        </v-col>

        <v-col cols="12">
          <v-row class="mx-2">
            <v-col>
              <v-autocomplete
                v-model="eventGroup"
                :items="itemise(Object.keys(typeReference))"
                dense
                outlined
                label="Group"
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-if="eventGroup && typeReference[eventGroup].qualifiers"
                v-model="qualifierType"
                :items="itemise(typeReference[eventGroup].qualifiers)"
                dense
                outlined
                label="Qualifier"
              />
            </v-col>
            <v-col>
              <v-autocomplete
                v-if="
                  qualifierType ||
                  (eventGroup && !typeReference[eventGroup].qualifiers)
                "
                v-model="eventType"
                :items="itemise(typeReference[eventGroup].events)"
                dense
                outlined
                label="Event"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <v-row class="mx-2" justify="center">
            <v-col align="start" cols="6" class="mt-n5">
              <v-chip
                v-for="eventKey in selectedData.keys"
                :key="eventKey"
                class="mx-2"
              >
                {{ eventFromKey(eventKey) }}
                <v-btn icon small class="ml-3" @click="removeEvent(eventKey)">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-chip>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-actions>

    <v-card-text v-if="selectedData.keys.length">
      <v-row justify="center">
        <v-col cols="6">
          <v-card color="white">
            <Bar id="ledger-chart" :data="chartData" :options="chartOptions" />
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { formatDate, endOfDay, capitalise } from "@/lib/utils";
import { mapActions, mapGetters } from "vuex";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import client from "@/lib/ApiClient";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
);

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const defaultStart = new Date(new Date().getTime() - 365 * DAY_IN_MS);
const defaultEnd = new Date();

export default {
  components: { Bar },

  data: () => ({
    loading: false,
    startDate: formatDate(defaultStart),
    endDate: formatDate(defaultEnd),
    frequency: "D",
    eventGroup: null,
    qualifierType: null,
    eventType: null,

    selectedData: {
      eventGroup: null,
      keys: [],
    },

    chartOptions: {
      responsive: true,
    },
  }),

  watch: {
    detailDialog(val) {
      if (!val) this.item = null;
    },

    eventGroup() {
      this.eventType = null;
      this.qualifierType = null;
      this.selectedData.eventGroup = null;
      this.selectedData.keys = [];
    },

    frequency() {
      this.refetchTimeSeries();
    },
  },

  computed: {
    ...mapGetters(["ledgerTimeSeries"]),
    items() {
      if (!this.quotaAggregate) return [];

      return Object.entries(this.quotaAggregate)?.map(([quota_code, a]) => ({
        code: quota_code,
        outstanding: a.outstanding,
        used: a.used,
        refunded: a.refunded,
        manuallyAdded: a.manuallyAdded,
        expiring: a.expiring["1"] || 0,
        details: a,
      }));
    },

    chartData() {
      if (!this.ledgerTimeSeries || !this.selectedData.keys.length) return;

      const object = JSON.parse(
        this.ledgerTimeSeries[this.selectedData.eventGroup]
      );

      let data = this.selectedData.keys.reduce((agg, key) => {
        for (let timestamp in object[key]) {
          agg[timestamp] = (agg[timestamp] || 0) + object[key][timestamp];
        }
        return agg;
      }, {});

      const processDate = (key) => {
        if (this.frequency === "D")
          return new Date(Number(key)).toLocaleDateString();
        else
          return new Date(Number(key)).toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          });
      };

      return {
        labels: Object.keys(data).map((key) => processDate(key)),
        datasets: [
          {
            label: this.selectedData.keys
              .map((k) => this.eventFromKey(k))
              .join(" "),
            backgroundColor: "#f87979",
            data: Object.values(data),
          },
        ],
      };
    },

    validSelection() {
      if (
        this.typeReference[this.eventGroup]?.qualifiers &&
        !this.qualifierType
      )
        return false;
      return !!(this.eventGroup && this.eventType);
    },

    typeReference() {
      const reference = {};

      if (!this.ledgerTimeSeries) return reference;

      Object.keys(this.ledgerTimeSeries)?.forEach((k) => {
        reference[k] = reference[k] || {};

        Object.keys(JSON.parse(this.ledgerTimeSeries[k])).forEach((k2) => {
          const arr = k2.replaceAll(/[()' ]/g, "").split(",");
          reference[k].events = reference[k].events || [];

          if (arr.length > 1) {
            reference[k].qualifiers = reference[k].qualifiers || [];
            if (!reference[k].qualifiers.includes(arr[1]))
              reference[k].qualifiers.push(arr[1]);
            if (!reference[k].events.includes(arr[2]))
              reference[k].events.push(arr[2]);
          } else if (!reference[k].events.includes(arr[0]))
            reference[k].events.push(arr[0]);
        });
      });

      return reference;
    },

    determinedLabel() {
      if (!this.validSelection) return;
      if (!this.qualifierType) return this.eventType;

      const string = `('${this.eventGroup}', '${this.qualifierType}', '${this.eventType}')`;
      return string;
    },
  },

  methods: {
    ...mapActions(["setLedgerTimeSeries"]),

    async refetchTimeSeries() {
      this.loading = true;
      this.setLedgerTimeSeries(
        await client.gateway.management.fetchLedgerTimeSeries(
          this.frequency,
          new Date(this.startDate).toISOString(),
          endOfDay(this.endDate).toISOString()
        )
      );
      this.loading = false;
    },

    addToChart() {
      this.selectedData.eventGroup =
        this.selectedData.eventGroup || this.eventGroup;
      this.selectedData.keys.push(this.determinedLabel);
      this.qualifierType = null;
      this.eventType = null;
    },

    itemise(array) {
      return array.map((x) => ({
        text: capitalise(x),
        value: x,
      }));
    },

    eventFromKey(key) {
      const arr = key.split(", ");
      if (arr.length === 1) return key;
      if (arr.length > 1) {
        const event = arr[arr.length - 1].slice(
          1,
          arr[arr.length - 1].length - 2
        );
        const qualifier = arr[arr.length - 2].slice(
          1,
          arr[arr.length - 2].length - 1
        );
        return `${qualifier} ${event}`;
      }
    },

    removeEvent(key) {
      const index = this.selectedData.keys.findIndex((k) => k === key);
      this.selectedData.keys.splice(index, 1);
      if (!this.selectedData.keys.length) this.selectedData.eventGroup = null;
    },
  },
};
</script>
